import { BookOpen, Stethoscope, Users, ArrowRight } from 'lucide-react';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, Tile } from '@/components/system';

/**
 * Filantropía — an honest commitment page, not a results page. MEC is pre-launch:
 * we state WHY community work matters to this company and WHERE it focuses, and we
 * invite organizations to reach out. No invented numbers, no vague "hundreds".
 */
const areas = [
  {
    icon: BookOpen,
    title: 'Educación.',
    text: 'Materiales y apoyo para escuelas y programas educativos en comunidades con menos recursos — porque un aula equipada cambia la trayectoria de un niño.',
  },
  {
    icon: Stethoscope,
    title: 'Salud.',
    text: 'Apoyo a la llegada de medicamentos e insumos esenciales a centros de salud, coordinado con las agencias y organizaciones que hacen ese trabajo cada día.',
  },
  {
    icon: Users,
    title: 'Comunidad.',
    text: 'Colaboración con organizaciones locales y proyectos comunitarios en Cuba, priorizando iniciativas con impacto directo y verificable.',
  },
];

export function Filantropia() {
  const heroRef = useReveal<HTMLElement>();
  const whyRef = useReveal<HTMLElement>();
  const areasRef = useReveal<HTMLElement>();
  const ctaRef = useReveal<HTMLElement>();

  return (
    <main className="bg-bg-light">
      <SEO
        title="Filantropía y Comunidad | Impacto Social en Cuba"
        description="El compromiso social de MEC: apoyo a educación, salud y proyectos comunitarios en Cuba, a través de la red de agencias que usan la plataforma. Si tienes un proyecto, escríbenos."
        path="/filantropia"
        keywords="filantropía Cuba, proyectos comunitarios Cuba, apoyo educación Cuba, impacto social Cuba"
      />

      {/* Hero */}
      <Section tone="white" ref={heroRef} className="pt-[120px] sm:pt-[150px] pb-16 sm:pb-20">
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Filantropía y comunidad</p>
            <Display data-reveal as="h1" size="xl">Detrás de cada paquete hay una familia.</Display>
            <Lead data-reveal className="mt-6 mx-auto max-w-2xl">
              MEC existe porque hay personas que cuidan a los suyos a 90 millas de distancia.
              Devolver una parte a esas comunidades no es un extra — es parte del trabajo.
            </Lead>
          </div>
        </Container>
      </Section>

      {/* Why / how */}
      <Section tone="dark" ref={whyRef} className="py-0 overflow-hidden">
        <div className="grid lg:grid-cols-2 items-stretch">
          <div className="relative min-h-[320px] lg:min-h-[520px]">
            <img
              src="/work_classroom.webp"
              alt="Aula en Cuba apoyada por un proyecto comunitario"
              loading="lazy"
              width="800"
              height="450"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="flex items-center px-6 sm:px-12 lg:px-16 py-16 lg:py-24">
            <div className="max-w-xl">
              <Display data-reveal size="md" className="text-white">
                Cómo lo hacemos.
              </Display>
              <div data-reveal className="mt-6 space-y-5 text-text-muted text-lg leading-relaxed">
                <p>
                  No somos una organización benéfica, y no pretendemos serlo. Somos una empresa
                  de software con una red de agencias que toca cientos de comunidades en Cuba —
                  y esa red también puede mover ayuda.
                </p>
                <p>
                  Trabajamos con organizaciones y personas que ya están haciendo el trabajo en
                  el terreno: aportamos recursos, coordinación y la logística de nuestros
                  socios para que el apoyo llegue a donde tiene que llegar.
                </p>
                <p className="text-white font-medium">
                  "Educar es sembrar esperanza para el futuro."
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Focus areas */}
      <Section tone="grey" ref={areasRef} className="py-16 sm:py-20">
        <Container>
          <Display data-reveal size="md" className="mb-4">Dónde ponemos el foco.</Display>
          <Lead data-reveal className="mb-10 max-w-2xl">
            Tres áreas, elegidas porque son donde un poco de apoyo cambia más vidas.
          </Lead>
          <div className="grid sm:grid-cols-3 gap-4">
            {areas.map((a) => (
              <Tile key={a.title} data-reveal className="p-7 sm:p-8">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                  <a.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{a.title}</h3>
                <p className="mt-2 text-text-secondary leading-relaxed">{a.text}</p>
              </Tile>
            ))}
          </div>
          <p data-reveal className="mt-8 text-sm text-text-tertiary max-w-2xl">
            A medida que los proyectos se concreten, publicaremos aquí en qué se trabajó y con
            quién — con resultados reales, no promesas.
          </p>
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="grey" ref={ctaRef} className="pt-0 pb-16 sm:pb-20">
        <Container>
          <div className="rounded-[32px] bg-white px-8 py-16 sm:px-12 sm:py-20 text-center">
            <Display data-reveal size="md">¿Tienes un proyecto?</Display>
            <Lead data-reveal className="mt-5 mx-auto max-w-2xl">
              Si representas una organización sin fines de lucro o trabajas en un proyecto
              comunitario en Cuba, nos encantaría escucharte.
            </Lead>
            <div data-reveal className="mt-10">
              <a
                href="mailto:filantropia@mienviocuba.com"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-medium px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors group"
              >
                filantropia@mienviocuba.com
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </main>
  );
}
